<script setup>
import { ref, onMounted, computed } from "vue";
import axios from 'axios';
import { useLoading } from 'vue-loading-overlay'
import Layout from "@/layouts/mainfw.vue";
import PageHeader from "@/components/page-header";
import { useFastWay } from "@/state/modules/fastway";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import { AgGridVue } from "ag-grid-vue3";
import Dialog from '@/views/components/Dialog.vue';
import {DropDownListComponent} from "@syncfusion/ej2-vue-dropdowns";

const store = useFastWay();
const loader = useLoading();
let dialog = ref(null);
let data = ref([]);
const title = "Resumen de Indicadores";
const items = [
  {
    text: "Tablero",
    href: "/",
  },
  {
    text: "Resumen de Indicadores",
    active: true,
  },
];

const api = computed({
    get() {
      return store.parameters.apiURL;
    }
});

const viewParms = computed({
  get() {
    return store.viewParameters.projectsMetricsSummary || { year: (new Date()).getFullYear() };
  }
});

const formatInteger = params => {
  let value = '';
  if (typeof params.value !== 'undefined') {
    if (params.value !== 0) {
      value = params.value.toLocaleString('es-GT', {minimumFractionDigits: 0, maximumFractionDigits: 0});
    }
  }
  return value;
}

const formatPercentage = params => {
  let value = '';
  if (typeof params.value !== 'undefined') {
    if (params.value !== 0) {
      value = params.value.toLocaleString('es-GT', {minimumFractionDigits: 2, maximumFractionDigits: 2}) + '%';
    }
  }
  return value;
}

const leftCellStyle = { backgroundColor: '#ffffff', color: '#000000', whiteSpace: 'normal' };
const rightCellStyle = { backgroundColor: '#ffffff', color: '#000000', textAlign: 'right' };

const columnDefs = [
  {
    headerName: 'Código Proyecto',
    field: 'projectCode',
    width: 130,
    sortable: true,
    pinned: 'left',
    cellStyle: leftCellStyle
  },
  {
    headerName: 'Proyecto',
    field: 'projectName',
    width: 250,
    sortable: true,
    pinned: 'left',
    cellStyle: leftCellStyle
  },
  {
    headerName: 'Logro Global',
    children: [
      { headerName: '%', field: 'globalAchievementPct', width: 110, cellStyle: rightCellStyle, valueFormatter: formatPercentage }
    ]
  },
  {
    headerName: 'Personas',
    children: [
      { headerName: 'Logro', field: 'peopleAchievement', width: 110, cellStyle: rightCellStyle, valueFormatter: formatInteger },
      { headerName: 'Meta', field: 'peopleGoal', width: 110, cellStyle: rightCellStyle, valueFormatter: formatInteger },
      { headerName: 'Pendiente', field: 'peopleRemaining', width: 110, cellStyle: rightCellStyle, valueFormatter: formatInteger },
      { headerName: '% Logro', field: 'peopleAchievementPct', width: 100, cellStyle: rightCellStyle, valueFormatter: formatPercentage },
      { headerName: '% Pendiente', field: 'peopleRemainingPct', width: 110, cellStyle: rightCellStyle, valueFormatter: formatPercentage }
    ]
  },
  {
    headerName: 'Escrituras',
    children: [
      { headerName: 'Entregadas', field: 'scripturesDelivered', width: 120, cellStyle: rightCellStyle, valueFormatter: formatInteger },
      { headerName: 'Meta', field: 'scripturesGoalTotal', width: 110, cellStyle: rightCellStyle, valueFormatter: formatInteger },
      { headerName: 'Pendientes', field: 'scripturesRemaining', width: 120, cellStyle: rightCellStyle, valueFormatter: formatInteger },
      { headerName: '% Logro', field: 'scripturesAchievementPct', width: 100, cellStyle: rightCellStyle, valueFormatter: formatPercentage },
      { headerName: '% Pendiente', field: 'scripturesRemainingPct', width: 110, cellStyle: rightCellStyle, valueFormatter: formatPercentage }
    ]
  }
];

onMounted(async () => {
  getData();
});

const getData = async () => {
  data.value = [];
  const loading = loader.show({loader: 'bars', color: '#0000FF'}, {after: () => 'Cargando resumen de indicadores'});
  const params = {
    userId: store.userInfo.userId,
    token: store.userInfo.token,
    year: viewParms.value.year
  };
  await axios
  .post(`${api.value}/sbg/projects/metrics/summary`, params)
  .then(response => {
    if (response.data.data) {
      data.value = response.data.data;
    } else {
      dialog.value.show('error', 'Error al cargar resumen de indicadores', response.data.message);
    }
    loading.hide();
  })
  .catch(error => {
    let errorMessage = '';
    if (error.message) {
      errorMessage = error.message;
    } else if (error.response) {
      errorMessage = error.response.data.message || error.response.data.ErrorMessage;
    } else {
      errorMessage = 'Error de conectividad al cargar resumen de indicadores';
    }
    loading.hide();
    dialog.value.show('error', 'Error al cargar resumen de indicadores', errorMessage);
  });
}

const years = computed(() => {
  const currentYear = new Date().getFullYear() + 1;
  return Array.from({ length: 6 }, (_, i) => currentYear - i);
});

const onYearChange = () => {
  getData();
}

</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
     <div class="row">
        <div class="col-lg-12">
            <div class="card">
                <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center align-middle row mb-2">
                        <div class="col-1">
                          <DropDownListComponent v-model:value="viewParms.year" floatLabelType="Auto" placeholder="Año" :dataSource="years" :change="onYearChange"/>
                        </div>
                        <div class="col d-flex justify-content-start">
                          <b-button variant="outline-success" class="btn-icon waves-effect waves-light me-1" v-b-tooltip.hover title="Recargar" @click="getData">
                            <i class="las la-sync"></i>
                          </b-button>
                        </div>
                    </div>
                    <ag-grid-vue
                        style="width: 100%; height: 700px;"
                        class="ag-theme-balham"
                        :columnDefs="columnDefs"
                        :rowData="data"
                        :accentedSort="true"
                    >
                    </ag-grid-vue>
                </div>
            </div>
        </div>
    </div>
    <Dialog ref="dialog"/>
  </Layout>
</template>

<style scoped>
@import "../../../../node_modules/@syncfusion/ej2-base/styles/material.css";
@import "../../../../node_modules/@syncfusion/ej2-vue-inputs/styles/material.css";
@import "../../../../node_modules/@syncfusion/ej2-vue-dropdowns/styles/material.css";

#input-container .e-input-group {
  margin: 30px 0;
}

.e-input-group-icon:before {
  font-family: e-icons;
}

.e-input-group .e-input-group-icon.e-input-clear .e-input-group-icon.e-input-search {
  font-size: 12px;
}

.e-input-group.e-small .e-input-group-icon.e-input-clear .e-input-group-icon.e-input-search {
  font-size: 12px;
}

.e-input-group-icon.e-input-clear:before {
  content: "\e7a7";
}

.e-input-group-icon.e-input-search:before {
  content: "\e993";
}
</style>
